import type { FailedPointCodeLog } from '~/types/api/client/pointshop'
import type { GetPointshopProductsResponse } from '~/types/api/pointshop/products'

export function parseFailedPointCodeDate(value: string | null | undefined): number {
  if (!value?.trim()) return 0

  const direct = Date.parse(value)
  if (!Number.isNaN(direct)) return direct

  const match = value.trim().match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2,4})$/)
  if (!match) return 0

  const [, day, month, yearPart] = match
  const year = yearPart.length === 2 ? `20${yearPart}` : yearPart
  const parsed = Date.parse(`${day} ${month} ${year}`)
  return Number.isNaN(parsed) ? 0 : parsed
}

export function formatFailedPointCodeDate(value: string | null | undefined): string {
  if (!value?.trim()) return 'Unknown date'

  const timestamp = parseFailedPointCodeDate(value)
  if (!timestamp) return value

  return new Date(timestamp).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function sortByDateLoggedDesc(logs: FailedPointCodeLog[]): FailedPointCodeLog[] {
  return [...logs].sort(
    (a, b) => parseFailedPointCodeDate(b.dateLogged) - parseFailedPointCodeDate(a.dateLogged)
  )
}

export function useFailedPointCodeReports(
  clientId: MaybeRefOrGetter<number>,
  wuId: MaybeRefOrGetter<number>
) {
  const reports = ref<FailedPointCodeLog[]>([])
  const loading = ref(false)

  const customerReports = computed(() =>
    sortByDateLoggedDesc(
      reports.value.filter(log => Number(log.usedByWebUserId) === toValue(wuId))
    )
  )

  const reportCount = computed(() => customerReports.value.length)

  const pendingReportCount = computed(() =>
    customerReports.value.filter(log => log.resolutionStatus === 'PEND').length
  )

  const hasReports = computed(() => reportCount.value > 0)

  const hasPendingReports = computed(() => pendingReportCount.value > 0)

  async function refresh() {
    const id = toValue(clientId)
    loading.value = true

    try {
      const productsResponse = await $fetch<GetPointshopProductsResponse>(
        '/api/client/webusers/pointshop/products',
        {
          query: { clientId: id },
          headers: useRequestHeaders(['cookie']) as HeadersInit,
        }
      )

      const client = productsResponse.clients?.find(c => c.clientId === id)
      const products = client?.pointshopProducts ?? productsResponse.clients?.[0]?.pointshopProducts ?? []

      if (!products.length) {
        reports.value = []
        return
      }

      const websiteIds = [...new Set(products.map(p => p.websiteId))]
      const results = await Promise.all(
        websiteIds.map(websiteId =>
          $fetch<FailedPointCodeLog[]>('/api/client/webusers/pointshop/failed-point-codes', {
            query: { clientId: id, websiteId },
            headers: useRequestHeaders(['cookie']) as HeadersInit,
          })
        )
      )

      reports.value = results.flat()
    } catch {
      reports.value = []
    } finally {
      loading.value = false
    }
  }

  return {
    reports,
    customerReports,
    reportCount,
    pendingReportCount,
    hasReports,
    hasPendingReports,
    loading,
    refresh,
  }
}
